import { Box, InputAdornment, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import uniqid from 'uniqid';
import { SemiProduct } from '../../apiTypes';
import EditModal from '../../components/EditModal';
import FormButtonSelect from '../../components/forms/FomsButtonSelect';
import FormComponentsTable, { FormComponentsTableProps } from '../../components/forms/FormComponentsTable';
import FormTextField from '../../components/forms/FormTextField';
import FormUploadImg from '../../components/FormUploadImg';
import StyledTextField from '../../components/styled/StyledTextField';
import { t } from '../../constants/translations';
import { useAdmin } from '../../contexts/admin.context';
import { useCafe } from '../../contexts/cafe.context';
import { useLocale } from '../../contexts/locale.context';
import { deleteDoc, setDoc, updateDoc } from '../../utils/api';
import EditStore from '../Stores/EditStore';

type Props = {
  open: boolean;
  semiProduct?: DbDocument<SemiProduct> | null;
  onClose: () => void;
};

const EditSemiProduct = ({ semiProduct, open, onClose }: Props) => {
  const { cafeSlug } = useAdmin();
  const { lang } = useLocale();
  const { ingredients } = useCafe();
  const [openAddStore, setOpenAddStore] = useState(false);

  const form = useForm<SemiProduct>({
    mode: 'onChange',
    defaultValues: {
      name: semiProduct?.name || '',
      unit: semiProduct?.unit || 'pc',
      imageUrl: semiProduct?.imageUrl || '',
      components: semiProduct?.components || [],
      batchVolume: semiProduct?.batchVolume || 1000,
    },
  });

  const unit = form.watch('unit') || semiProduct?.unit || 'pc';
  const batchVolume = form.watch('batchVolume');

  const componentOptions = useMemo(
    () => ingredients.filter((i) => i.id !== semiProduct?.id),
    [ingredients, semiProduct?.id]
  );

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const saveChanges = (newSemiProduct: SemiProduct) => {
    const validSemiProduct: SemiProduct = {
      ...newSemiProduct,
      components: newSemiProduct.components.filter((c) => !!c.qnt),
    };

    if (semiProduct?.id) {
      updateDoc<SemiProduct>(`cafes/${cafeSlug}/semiProducts/${semiProduct.id}`, validSemiProduct);
    } else {
      setDoc<SemiProduct>(`cafes/${cafeSlug}/semiProducts/${uniqid()}`, validSemiProduct);
    }

    handleClose();
  };

  const removeProduct = async () => {
    if (semiProduct?.id) {
      // stores.forEach((store) => {
      //   updateDoc(doc(db, "company", companySlug, "stores", store.id), {
      //     [`residues.${consumable.id}`]: deleteField(),
      //   });
      // });
      deleteDoc(`cafes/${cafeSlug}/semiProducts/${semiProduct.id}`);
      handleClose();
    }
  };

  return (
    <>
      <EditModal<SemiProduct>
        title={t.semifinished_product[lang]}
        open={open}
        onClose={handleClose}
        submitButton={semiProduct ? t.save_changes[lang] : t.add[lang]}
        onSubmit={saveChanges}
        secondaryButton={semiProduct ? t.remove[lang] : undefined}
        confirmText={t.remove_product_confirm[lang]}
        onSecondaryButtonClick={removeProduct}
        form={form}
        size="sm"
      >
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 2, mb: 1, mx: 'auto' }}>
          <Box sx={{ mr: 3, flexShrink: 0 }}>
            <FormUploadImg name="imageUrl" defaultValue={semiProduct?.imageUrl || ''} folder="semiProducts" />
          </Box>

          <Box sx={{ width: '100%' }}>
            <FormTextField name="name" label={t.name[lang]} rules={{ required: true }} fullWidth sx={{ mb: 2 }} />

            <FormButtonSelect name="unit" label={t.unit[lang]} options={['pc', 'g', 'ml']} />
          </Box>
        </Box>

        <Typography sx={{ color: (t) => t.palette.text.secondary, mb: 1 }}>
          {`${t.costs_of_production[lang]}:`}
        </Typography>

        <FormComponentsTable
          form={form as unknown as FormComponentsTableProps['form']}
          options={componentOptions}
          shouldSelectStore
          handleAddStore={() => setOpenAddStore(true)}
          disablePackingInit
        />

        <Box sx={{ textAlign: 'right' }}>
          <StyledTextField
            variant="outlined"
            label={t.output_volume[lang]}
            type="number"
            name="output-volume"
            value={String(batchVolume)}
            onChange={(e) => form.setValue('batchVolume', Number(e.target.value))}
            InputProps={{
              endAdornment: <InputAdornment position="start">{t[unit][lang]}</InputAdornment>,
            }}
          />
        </Box>
      </EditModal>

      {openAddStore && <EditStore open onClose={() => setOpenAddStore(false)} />}
    </>
  );
};

export default EditSemiProduct;
